import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useAdminEventsWithCounts } from "@/hooks/useEvents";
import { formatEventDate, formatEventTime, type EventWithCount } from "@/lib/events";
import AdminEventSignupsList from "@/components/admin/AdminEventSignupsList";

const AdminEvents = () => {
  const { data: events, isLoading, error } = useAdminEventsWithCounts();
  const [signupsFor, setSignupsFor] = useState<EventWithCount | null>(null);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold">Arrangementer</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Opprett og publiser arrangementer som vises på forsiden og under /arrangementer.
          </p>
        </div>
        <Button asChild>
          <Link to="/admin/events/ny">
            <Plus className="mr-2 h-4 w-4" />
            Nytt arrangement
          </Link>
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Laster …</p>
      ) : error ? (
        <p className="text-sm text-destructive">Kunne ikke laste arrangementer.</p>
      ) : !events || events.length === 0 ? (
        <div className="rounded-lg border bg-card p-12 text-center">
          <p className="text-muted-foreground">
            Ingen arrangementer enda. Opprett ditt første for å vise det på siden.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border bg-card">
          <table className="w-full">
            <thead className="border-b bg-muted/30">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Tittel</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Dato</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Sted</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Påmeldte</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y">
              {events.map((event) => {
                const cap = event.max_attendees;
                const taken = event.signup_count;
                const signupOff = !event.enable_signup;
                return (
                  <tr key={event.id} className="hover:bg-muted/20">
                    <td className="px-4 py-3 font-medium">{event.title}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {formatEventDate(event.starts_at)} kl. {formatEventTime(event.starts_at)}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {event.location ?? "—"}
                    </td>
                    <td className="px-4 py-3">
                      {signupOff && taken === 0 ? (
                        <span className="text-sm text-muted-foreground">Påmelding av</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setSignupsFor(event)}
                          className="inline-flex items-center gap-2 rounded-md px-2 py-1 text-sm font-medium hover:bg-muted/60"
                          title="Vis påmeldte"
                        >
                          <Users className="h-3.5 w-3.5 text-primary" />
                          {cap != null ? (
                            <span>
                              {taken} / {cap}
                              {taken >= cap && (
                                <span className="ml-1 text-xs text-destructive">fullt</span>
                              )}
                            </span>
                          ) : (
                            <span>{taken}</span>
                          )}
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {event.is_published ? (
                        <Badge variant="default">Publisert</Badge>
                      ) : (
                        <Badge variant="secondary">Utkast</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="ghost" size="sm" asChild>
                        <Link to={`/admin/events/${event.id}`}>Rediger</Link>
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={Boolean(signupsFor)} onOpenChange={(open) => !open && setSignupsFor(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{signupsFor?.title}</DialogTitle>
            <DialogDescription>
              {signupsFor &&
                `${formatEventDate(signupsFor.starts_at)} kl. ${formatEventTime(signupsFor.starts_at)}`}
            </DialogDescription>
          </DialogHeader>
          {signupsFor && <AdminEventSignupsList eventId={signupsFor.id} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminEvents;
